/**
 * Where each backend service lives.
 *
 * The app is moving from the legacy hosts (`https://{dev|prod}.aimlapps.com`,
 * one `/{name}-svc/` prefix per service) to the V2 platform
 * (`https://api.{dev.|}optimal-hr.com`, one `/{service}/v1/` prefix per
 * service). The move is per service, not all at once: `employment` and
 * `identity` answer on V2 first, everything else keeps talking to the legacy
 * host until its own slice ships.
 *
 * Three env keys drive it:
 *
 * - `VITE_API_URL`          the legacy host. Unchanged.
 * - `VITE_API_V2_URL`       the V2 host. **Empty means "everything legacy"** —
 *                           that is the one-line kill switch for a whole env.
 * - `VITE_API_V2_SERVICES`  comma list of the services that are on V2.
 *                           Defaults to `employment,identity,schedule`. Removing a name
 *                           sends that service back to the legacy host without
 *                           touching any code.
 *
 * Nothing here knows about axios. `httpClient` keeps its single instance and
 * asks `baseUrlForPath()` per request which host that request belongs to.
 */

/** Every backend service the web app talks to. */
export const API_SERVICES = [
  'employment',
  'identity',
  'iam',
  'schedule',
  'tams',
  'leave',
  'ot',
  'payroll',
  'pim',
  'srm',
  'directory',
  'perform',
  'audit',
] as const;

export type ApiService = (typeof API_SERVICES)[number];

/**
 * The URL prefix each service answers on once it is on V2. Only the services
 * that exist on the V2 platform have one; the rest can never be flipped, so
 * naming them in `VITE_API_V2_SERVICES` does nothing.
 */
export const V2_PATH_PREFIXES: Readonly<Partial<Record<ApiService, string>>> = Object.freeze({
  employment: '/employment/',
  identity: '/identity/',
  schedule: '/schedule/',
  // The audit trail (Edit History). V2-only: there is no legacy audit backend,
  // so it is not in DEFAULT_V2_SERVICES — an env opts in by naming it in
  // VITE_API_V2_SERVICES (development does; production does not).
  audit: '/audit/',
});

/** Services that are on V2 when `VITE_API_V2_SERVICES` is not set. */
export const DEFAULT_V2_SERVICES = 'employment,identity,schedule';

/** The three env values this module reads. Passed in explicitly so it is testable. */
export interface ApiHostEnv {
  /** `VITE_API_URL` — the legacy host. */
  legacyUrl: string;
  /** `VITE_API_V2_URL` — the V2 host; empty or missing means "everything legacy". */
  v2Url?: string | undefined;
  /** `VITE_API_V2_SERVICES` — comma list; undefined falls back to {@link DEFAULT_V2_SERVICES}. */
  v2Services?: string | undefined;
}

/**
 * Read the host env out of `import.meta.env`.
 *
 * Read per call rather than once at module load so a test can stub the env and
 * so a future runtime-config path has one place to change.
 * @returns The three host env values.
 */
export function readApiHostEnv(): ApiHostEnv {
  return {
    legacyUrl: import.meta.env.VITE_API_URL,
    v2Url: import.meta.env.VITE_API_V2_URL,
    v2Services: import.meta.env.VITE_API_V2_SERVICES,
  };
}

/**
 * Parse the comma-separated kill-switch list into the set of services that are
 * on V2. Unknown names and services with no V2 prefix are dropped; whitespace
 * and case are forgiven. `undefined` means "use the default list"; an empty
 * string means "no service is on V2".
 * @param raw The raw `VITE_API_V2_SERVICES` value.
 * @returns The set of services to route to the V2 host.
 */
export function parseV2Services(raw: string | undefined): Set<ApiService> {
  const source = raw === undefined ? DEFAULT_V2_SERVICES : raw;
  const wanted = source
    .split(',')
    .map((part) => part.trim().toLowerCase())
    .filter(Boolean);
  const enabled = new Set<ApiService>();
  for (const name of wanted) {
    const service = API_SERVICES.find((s) => s === name);
    if (service && V2_PATH_PREFIXES[service]) enabled.add(service);
  }
  return enabled;
}

/**
 * Whether a service's calls go to the V2 host.
 * @param service The service to check.
 * @param env The host env.
 * @returns `true` when the V2 host is configured and the service is switched on.
 */
export function isV2Service(service: ApiService, env: ApiHostEnv): boolean {
  if (!env.v2Url) return false;
  return parseV2Services(env.v2Services).has(service);
}

/**
 * The full base-path map: every service to the host it currently answers on.
 * @param env The host env.
 * @returns One base URL per service.
 */
export function resolveApiHosts(env: ApiHostEnv): Record<ApiService, string> {
  const onV2 = env.v2Url ? parseV2Services(env.v2Services) : new Set<ApiService>();
  const map = {} as Record<ApiService, string>;
  for (const service of API_SERVICES) {
    map[service] = onV2.has(service) ? (env.v2Url as string) : env.legacyUrl;
  }
  return map;
}

/**
 * Which V2 service a request URL belongs to, by its path prefix.
 *
 * Only matches a V2-shaped path (`/employment/…`, `/identity/…`, `/schedule/…`, `/audit/…`); a legacy
 * `/employment-svc/…` URL is not a match, and neither is an absolute URL — a
 * call that already carries its own host is left alone.
 * @param url The request URL as written by the caller.
 * @returns The service, or `undefined` when the URL is not a V2 path.
 */
export function v2ServiceForPath(url: string | undefined): ApiService | undefined {
  if (!url || !url.startsWith('/')) return undefined;
  for (const service of API_SERVICES) {
    const prefix = V2_PATH_PREFIXES[service];
    if (prefix && url.startsWith(prefix)) return service;
  }
  return undefined;
}

/**
 * The base URL a request with this path must be sent to.
 *
 * A V2-shaped path whose service is switched on goes to the V2 host; anything
 * else — a legacy `/{name}-svc/` path, an unswitched service, or a V2 path
 * while `VITE_API_V2_URL` is empty — goes to the legacy host, exactly as before.
 * @param url The request URL as written by the caller.
 * @param env The host env; defaults to `import.meta.env`.
 * @returns The base URL to use.
 */
export function baseUrlForPath(
  url: string | undefined,
  env: ApiHostEnv = readApiHostEnv(),
): string {
  const service = v2ServiceForPath(url);
  if (service && isV2Service(service, env)) return env.v2Url as string;
  return env.legacyUrl;
}
